// src/utils/tableBuilders.js

import { applyFormatToNumber } from "./formatUtils";
import { filterObjectsByProperty, getValuesByPropertyPath } from "./dataUtils";
import { showTesting } from "./debugUtils";

/**
 * Returns the full HTML string for the Summary Table.
 * @param {object} scenario - The scenario object (currScenario)
 * @param {object} deltaChartData - The chart data (labels/colors), optional
 * @returns {string} Table HTML
 */
function buildSummaryTableHTML(scenario, deltaChartData) {
	if (!scenario || !deltaChartData) return "";

	const dec = scenario.currency_display_decimal;
	const sym = scenario.currency_symbol;
	const unit = scenario.currency_unit;

	const showTest = showTesting?.() || false;

	let html = `<div id="summaryTableDiv"><table class="chartjsTable">`;

	// --- Header ---
	html += `<thead class="chartjs-thead"><tr>`;
	html += `<th></th>`;
	deltaChartData.labels.forEach((label) => {
		html += `<th>${label}</th>`;
	});
	html += `<th> Grand Total</th>`;
	html += `</tr></thead>`;

	// --- Body ---
	html += `<tbody class="chartjs-tbody">`;

	// Row 0: Starting Baseline
	html += `<tr><td>Starting Baseline</td>`;
	scenario.baseline_total_budget.forEach((y) => {
		html += `<td>`;
		html += applyFormatToNumber(y, dec, sym, unit, true);
		if (showTest) html += `<br>${y}`;
		html += `</td>`;
	});
	let totalBaseline = scenario.baseline_total_budget.reduce((a, b) => a + b, 0);
	html += `<td>`;
	html += applyFormatToNumber(totalBaseline, dec, sym, unit, true);
	if (showTest) html += `<br>${totalBaseline}`;
	html += `</td></tr>`;

	// Row 1: Targeted Budget
	html += `<tr><td>Targeted Budget</td>`;
	const targetBaseline = filterObjectsByProperty(scenario.scenario_configs, "comp_type_id", 11);
	scenario.baseline_total_budget.forEach((y, idx) => {
		let t = typeof targetBaseline[idx] !== "undefined" ? targetBaseline[idx].comp_value1 : 0;
		html += `<td>`;
		html += applyFormatToNumber(y * 1 + t * 1, dec, sym, unit, true);
		if (showTest) html += `<br>${y * 1 + t * 1}`;
		html += `</td>`;
	});
	let targetTotal =
		totalBaseline +
		getValuesByPropertyPath(targetBaseline, "comp_value1").reduce((a, b) => a * 1 + b * 1, 0);
	html += `<td>`;
	html += applyFormatToNumber(targetTotal, dec, sym, unit, true);
	if (showTest) html += `<br>${targetTotal}`;
	html += `</td></tr>`;

	// Row 2: Topline Changes
	html += `<tr><td>Topline Changes</td>`;
	scenario.baseline_change_in_budget.forEach((y) => {
		html += `<td>`;
		html += applyFormatToNumber(y, dec, sym, unit, true);
		if (showTest) html += `<br>${y}`;
		html += `</td>`;
	});
	let totalChange = scenario.baseline_change_in_budget.reduce((a, b) => a + b, 0);
	html += `<td>`;
	html += applyFormatToNumber(totalChange, dec, sym, unit, true);
	if (showTest) html += `<br>${totalChange}`;
	html += `</td></tr>`;

	html += `</tbody></table></div>`;
	return html;
}

/**
 * Builds an HTML string for the delta (change) table used in ScenarioBudgetSummary.
 * @param {object} currScenario - The current scenario object.
 * @param {object} deltaChartData - Chart data with labels, datasets, etc.
 * @returns {string} HTML string for the delta table.
 */
function buildDeltaTableHTML(currScenario, deltaChartData) {
	if (!currScenario || !deltaChartData) return "";

	// Helper fallback (optional, or import directly)
	const getContrastYIQ = window.getContrastYIQ || ((hex) => true);
	const mColor = window.mColor || { White: "#fff" }; // use your import here
	const showTest = typeof window.showTesting === "function" ? window.showTesting() : false;
	const applyFormatToNumber = window.applyFormatToNumber || ((v) => v);
	const applySignToNumber = window.applySignToNumber || ((v) => v);

	let html = `<div id="chartTableDiv"><table class="chartjsTable">`;

	// Header
	html += `<thead class="chartjs-thead"><tr>`;
	deltaChartData.labels.forEach((label, idx) => {
		html += `<th>${label}</th>`;
	});
	html += `<th></th><th>Grand Total</th></tr></thead>`;

	// Body
	html += `<tbody class="chartjs-tbody">`;

	deltaChartData.datasets.forEach((ds, idx1) => {
		html += `<tr>`;
		// Insert per-period values
		ds.data.forEach((y, idx2) => {
			html += `<td>`;
			html += applyFormatToNumber(
				y,
				currScenario.currency_display_decimal,
				currScenario.currency_symbol,
				currScenario.currency_unit,
				true
			);
			if (showTest) {
				html += `<br>${y}`;
			}
			html += `<br><br>Qty: ${applySignToNumber(ds.quantity[idx2])}`;
			html += `</td>`;
		});
		// Insert row label with background color
		html += `<td style="background:${ds.backgroundColor};${
			getContrastYIQ(ds.backgroundColor) ? "" : "color:" + mColor.White + ";"
		}">${ds.label}</td>`;

		// Insert row total and percent of baseline
		const total = ds.data.reduce((a, b) => a + b, 0);
		const baselineTotal = currScenario.baseline_total_budget.reduce((a, b) => a + b, 0);
		html += `<td>`;
		html += applyFormatToNumber(
			total,
			currScenario.currency_display_decimal,
			currScenario.currency_symbol,
			currScenario.currency_unit,
			true
		);
		if (showTest) {
			html += `<br>${total}`;
		}
		html += ` (${applySignToNumber(((total / baselineTotal) * 100).toFixed(2))}%)`;
		html += `<br><br>Qty: ${applySignToNumber(ds.quantityTotal)}`;
		html += `</td>`;

		html += `</tr>`;
	});

	html += `</tbody></table></div>`;
	return html;
}

/**
 * Pure builder: Builds and returns a <table> element for the summary table.
 * Never touches the DOM directly!
 */
export function buildSummaryTable({
	currScenario,
	deltaChartData,
	applyFormatToNumber,
	showTesting,
	filterObjectsByProperty,
	getValuesByPropertyPath,
}) {
	const table = document.createElement("TABLE");
	table.classList.add("chartjsTable");

	// --- THEAD ---
	const thead = table.createTHead();
	thead.classList.add("chartjs-thead");
	thead.insertRow(0);
	deltaChartData.labels.forEach((x, idx) => {
		thead.rows[0].insertCell(idx).innerText = x;
		return false;
	});
	thead.rows[0].insertCell(0).innerText = "";
	thead.rows[0].insertCell(-1).innerText = " Grand Total";

	// --- TBODY ---
	const tbody = table.createTBody();
	tbody.classList.add("chartjs-tbody");

	// Row 1: Starting Baseline
	tbody.insertRow(0);
	tbody.rows[0].insertCell(0).innerText = "Starting Baseline";
	currScenario.baseline_total_budget.forEach((y, idx2) => {
		tbody.rows[0].insertCell(idx2 + 1).innerText = showTesting()
			? applyFormatToNumber(
					y,
					currScenario.currency_display_decimal,
					currScenario.currency_symbol,
					currScenario.currency_unit,
					true
			  ) +
			  "\n" +
			  y
			: applyFormatToNumber(
					y,
					currScenario.currency_display_decimal,
					currScenario.currency_symbol,
					currScenario.currency_unit,
					true
			  );
		return false;
	});
	// Row total
	tbody.rows[0].insertCell(-1).innerText = showTesting()
		? applyFormatToNumber(
				currScenario.baseline_total_budget.reduce((a, b) => a + b, 0),
				currScenario.currency_display_decimal,
				currScenario.currency_symbol,
				currScenario.currency_unit,
				true
		  ) +
		  "\n" +
		  currScenario.baseline_total_budget.reduce((a, b) => a + b, 0)
		: applyFormatToNumber(
				currScenario.baseline_total_budget.reduce((a, b) => a + b, 0),
				currScenario.currency_display_decimal,
				currScenario.currency_symbol,
				currScenario.currency_unit,
				true
		  );

	// Row 2: Targeted Budget
	tbody.insertRow(1);
	tbody.rows[1].insertCell(0).innerText = "Targeted Budget";
	let targetBaseline = filterObjectsByProperty(currScenario.scenario_configs, "comp_type_id", 11);
	currScenario.baseline_total_budget.forEach((y, idx2) => {
		let t = typeof targetBaseline[idx2] !== "undefined" ? targetBaseline[idx2].comp_value1 : 0;
		tbody.rows[1].insertCell(idx2 + 1).innerText = showTesting()
			? applyFormatToNumber(
					y * 1 + t * 1,
					currScenario.currency_display_decimal,
					currScenario.currency_symbol,
					currScenario.currency_unit,
					true
			  ) +
			  "\n" +
			  (y * 1 + t * 1)
			: applyFormatToNumber(
					y * 1 + t * 1,
					currScenario.currency_display_decimal,
					currScenario.currency_symbol,
					currScenario.currency_unit,
					true
			  );
		return false;
	});
	tbody.rows[1].insertCell(-1).innerText = showTesting()
		? applyFormatToNumber(
				currScenario.baseline_total_budget.reduce((a, b) => a + b, 0) +
					getValuesByPropertyPath(targetBaseline, "comp_value1").reduce((a, b) => a * 1 + b * 1, 0),
				currScenario.currency_display_decimal,
				currScenario.currency_symbol,
				currScenario.currency_unit,
				true
		  ) +
		  "\n" +
		  (currScenario.baseline_total_budget.reduce((a, b) => a + b, 0) +
				getValuesByPropertyPath(targetBaseline, "comp_value1").reduce((a, b) => a * 1 + b * 1, 0))
		: applyFormatToNumber(
				currScenario.baseline_total_budget.reduce((a, b) => a + b, 0) +
					getValuesByPropertyPath(targetBaseline, "comp_value1").reduce((a, b) => a * 1 + b * 1, 0),
				currScenario.currency_display_decimal,
				currScenario.currency_symbol,
				currScenario.currency_unit,
				true
		  );

	// Row 3: Topline Changes
	tbody.insertRow(2);
	tbody.rows[2].insertCell(0).innerText = "Topline Changes";
	currScenario.baseline_change_in_budget.forEach((y, idx2) => {
		tbody.rows[2].insertCell(idx2 + 1).innerText = showTesting()
			? applyFormatToNumber(
					y,
					currScenario.currency_display_decimal,
					currScenario.currency_symbol,
					currScenario.currency_unit,
					true
			  ) +
			  "\n" +
			  y
			: applyFormatToNumber(
					y,
					currScenario.currency_display_decimal,
					currScenario.currency_symbol,
					currScenario.currency_unit,
					true
			  );
		return false;
	});
	tbody.rows[2].insertCell(-1).innerText = showTesting()
		? applyFormatToNumber(
				currScenario.baseline_change_in_budget.reduce((a, b) => a + b, 0),
				currScenario.currency_display_decimal,
				currScenario.currency_symbol,
				currScenario.currency_unit,
				true
		  ) +
		  "\n" +
		  currScenario.baseline_change_in_budget.reduce((a, b) => a + b, 0)
		: applyFormatToNumber(
				currScenario.baseline_change_in_budget.reduce((a, b) => a + b, 0),
				currScenario.currency_display_decimal,
				currScenario.currency_symbol,
				currScenario.currency_unit,
				true
		  );

	// Attach
	table.appendChild(thead);
	table.appendChild(tbody);
	return table;
}

/**
 * Build the Delta Table HTML element.
 * This matches the output/logic of your posted createDeltaTable.
 */
export function buildDeltaTable({
	deltaChartData,
	currScenario,
	mColor,
	getContrastYIQ,
	applyFormatToNumber,
	applySignToNumber,
	showTesting,
}) {
	const table = document.createElement("TABLE");
	table.classList.add("chartjsTable");

	// ----- THEAD -----
	const thead = table.createTHead();
	thead.classList.add("chartjs-thead");
	thead.insertRow(0);

	// Insert period headers
	deltaChartData.labels.map((x, idx) => {
		thead.rows[0].insertCell(idx).innerText = x;
		return false;
	});
	// First cell: blank (row label col)
	thead.rows[0].insertCell(0).innerText = "";
	// Last cell: Grand Total
	thead.rows[0].insertCell(-1).innerText = " Grand Total";

	// ----- TBODY -----
	const tbody = table.createTBody();
	tbody.classList.add("chartjs-tbody");

	deltaChartData.datasets.map((x, idx1) => {
		tbody.insertRow(idx1);

		x.data.map((y, idx2) => {
			const value = typeof y === "number" && !isNaN(y) ? y : 0;
			const qty =
				x.quantity && typeof x.quantity[idx2] !== "undefined" && !isNaN(x.quantity[idx2])
					? x.quantity[idx2]
					: 0;
			if (!showTesting()) {
				tbody.rows[idx1].insertCell(idx2).innerText =
					applyFormatToNumber(
						value,
						currScenario.currency_display_decimal,
						currScenario.currency_symbol,
						currScenario.currency_unit,
						true
					) +
					"\n\nQty: " +
					applySignToNumber(qty);
			} else {
				tbody.rows[idx1].insertCell(idx2).innerText =
					applyFormatToNumber(
						value,
						currScenario.currency_display_decimal,
						currScenario.currency_symbol,
						currScenario.currency_unit,
						true
					) +
					"\n" +
					value +
					"\n\nQty: " +
					applySignToNumber(qty);
			}
			return false;
		});
		// Insert label as first cell in row (row label)
		tbody.rows[idx1].insertCell(0).innerText = x.label;
		tbody.rows[idx1].cells[0].style.backgroundColor = x.backgroundColor;
		if (!getContrastYIQ(x.backgroundColor)) {
			tbody.rows[idx1].cells[0].style.color = mColor.White;
		}

		// Insert Grand Total cell at end
		const dataSum = x.data.reduce((a, b) => (isNaN(b) ? a : a + b), 0);
		const qtyTotal =
			typeof x.quantityTotal === "number" && !isNaN(x.quantityTotal)
				? x.quantityTotal
				: x.quantity
				? x.quantity.reduce((a, b) => (isNaN(b) ? a : a + b), 0)
				: 0;
		const baseSum = currScenario.baseline_total_budget
			? currScenario.baseline_total_budget.reduce((a, b) => (isNaN(b) ? a : a + b), 0)
			: 0;
		const percent = baseSum !== 0 ? ((dataSum / baseSum) * 100).toFixed(2) : "0.00";

		if (!showTesting()) {
			tbody.rows[idx1].insertCell(-1).innerText =
				applyFormatToNumber(
					dataSum,
					currScenario.currency_display_decimal,
					currScenario.currency_symbol,
					currScenario.currency_unit,
					true
				) +
				" (" +
				applySignToNumber(percent) +
				"%)" +
				"\n\nQty: " +
				applySignToNumber(qtyTotal);
		} else {
			tbody.rows[idx1].insertCell(-1).innerText =
				applyFormatToNumber(
					dataSum,
					currScenario.currency_display_decimal,
					currScenario.currency_symbol,
					currScenario.currency_unit,
					true
				) +
				"\n" +
				dataSum +
				"\n\nQty: " +
				applySignToNumber(qtyTotal);
		}
		return false;
	});

	return table;
}

/**
 * Returns a <table> element representing the chart data.
 */
export function buildChartTable({
	chartData,
	chartBy,
	chartByFy,
	currScenario,
	applyFormatToNumber,
	showTesting,
	getContrastYIQ,
	mColor,
}) {
	const table = document.createElement("TABLE");
	table.classList.add("chartjsTable");

	// --- THEAD ---
	const thead = table.createTHead();
	thead.classList.add("chartjs-thead");
	thead.insertRow(0);
	chartData.labels.forEach((x, idx) => {
		thead.rows[0].insertCell(idx).innerText = x;
		if (parseInt(chartBy) !== 11 && !chartByFy) {
			thead.rows[0].cells[idx].style.backgroundColor = chartData.colors[idx];
			if (!getContrastYIQ(chartData.colors[idx])) {
				thead.rows[0].cells[idx].style.color = mColor.White;
			}
		}
		return false;
	});
	thead.rows[0].insertCell(0).innerText = "";
	thead.rows[0].insertCell(-1).innerText = " Grand Total";

	// --- TBODY ---
	const tbody = table.createTBody();
	tbody.classList.add("chartjs-tbody");

	chartData.datasets.forEach((x, idx1) => {
		tbody.insertRow(idx1);
		x.data.forEach((y, idx2) => {
			if (!showTesting()) {
				tbody.rows[idx1].insertCell(idx2).innerText = applyFormatToNumber(
					y,
					currScenario.currency_display_decimal,
					currScenario.currency_symbol,
					currScenario.currency_unit,
					true
				);
			} else {
				tbody.rows[idx1].insertCell(idx2).innerText =
					applyFormatToNumber(
						y,
						currScenario.currency_display_decimal,
						currScenario.currency_symbol,
						currScenario.currency_unit,
						true
					) +
					"\n" +
					y;
			}
			return false;
		});
		tbody.rows[idx1].insertCell(0).innerText = x.label;
		if (parseInt(chartBy) !== 11 && chartByFy) {
			tbody.rows[idx1].cells[0].style.backgroundColor = x.backgroundColor;
			if (!getContrastYIQ(x.backgroundColor)) {
				tbody.rows[idx1].cells[0].style.color = mColor.White;
			}
		}
		// row total
		if (!showTesting()) {
			tbody.rows[idx1].insertCell(-1).innerText = applyFormatToNumber(
				x.data.reduce((a, b) => a + b, 0),
				currScenario.currency_display_decimal,
				currScenario.currency_symbol,
				currScenario.currency_unit,
				true
			);
		} else {
			tbody.rows[idx1].insertCell(-1).innerText =
				applyFormatToNumber(
					x.data.reduce((a, b) => a + b, 0),
					currScenario.currency_display_decimal,
					currScenario.currency_symbol,
					currScenario.currency_unit,
					true
				) +
				"\n" +
				x.data.reduce((a, b) => a + b, 0);
		}
		return false;
	});

	// display column total (if needed)
	if (parseInt(chartBy) !== 11 && chartByFy) {
		tbody.insertRow();
		const r = tbody.rows[chartData.datasets.length];
		r.insertCell(0).innerText = "Total";
		currScenario.baseline_change_in_budget.forEach((x, idx) => {
			if (!showTesting()) {
				r.insertCell(idx + 1).innerText = applyFormatToNumber(
					x,
					currScenario.currency_display_decimal,
					currScenario.currency_symbol,
					currScenario.currency_unit,
					true
				);
			} else {
				r.insertCell(idx + 1).innerText =
					applyFormatToNumber(
						x,
						currScenario.currency_display_decimal,
						currScenario.currency_symbol,
						currScenario.currency_unit,
						true
					) +
					"\n" +
					x;
			}
			return false;
		});
		if (!showTesting()) {
			r.insertCell(-1).innerText = applyFormatToNumber(
				currScenario.baseline_change_in_budget.reduce((a, b) => a + b, 0),
				currScenario.currency_display_decimal,
				currScenario.currency_symbol,
				currScenario.currency_unit,
				true
			);
		} else {
			r.insertCell(-1).innerText =
				applyFormatToNumber(
					currScenario.baseline_change_in_budget.reduce((a, b) => a + b, 0),
					currScenario.currency_display_decimal,
					currScenario.currency_symbol,
					currScenario.currency_unit,
					true
				) +
				"\n" +
				currScenario.baseline_change_in_budget.reduce((a, b) => a + b, 0);
		}
	}

	table.appendChild(thead);
	table.appendChild(tbody);
	return table;
}
